import {MockToolProps} from "./types";
import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {Badge, Checkbox, classNames, layout, useAsync} from "@variocube/cube-ui";
import {Tool} from "./Tool";
import {AwesomeBarItem} from "../AwesomeBarItem";
import {stylesheet} from "typestyle";
import {LockStatus} from "@variocube/app-api";
import {MockController} from "../controller/MockController";


export function LockingTool({mockController, align}: MockToolProps) {
    const [lockStatus, setLockStatus] = useState<Record<string, LockStatus>>({});
    const [failOnOpen, setFailOnOpen] = useState(mockController.failOnOpen);
    const [ignoreOpen, setIgnoreOpen] = useState(mockController.ignoreOpen);
    const locks = useAsync(() => mockController.getLocks(), [mockController]);

    useEffect(() => {
        mockController.failOnOpen = failOnOpen;
    }, [mockController, failOnOpen]);

    useEffect(() => {
        mockController.ignoreOpen = ignoreOpen;
    }, [mockController, ignoreOpen]);

    useEffect(() => {
        if (locks.result) {
            setLockStatus(locks.result.reduce((acc, next) => ({
                ...acc, [next.handle]: next.status
            }), {} as Record<string, LockStatus>));
        }
    }, [locks.result]);

    const handleLockStatusChange = useCallback((lock: string, status: LockStatus) => {
        setLockStatus(lockStatus => ({...lockStatus, [lock]: status}));
    }, []);

    useEffect(() => {
        mockController.addLockStatusListener(handleLockStatusChange);
        return () => mockController.removeLockStatusListener(handleLockStatusChange);
    }, [mockController, handleLockStatusChange]);

    const anyOpen = Object.values(lockStatus).some(status => status != LockStatus.Closed);

    return (
        <Tool label="Locking" highlight={anyOpen} align={align}>
            <div className={layout.paddingMedium}>
                <Checkbox checked={failOnOpen} onChange={setFailOnOpen}>
                    Let open commands fail
                </Checkbox>
                <Checkbox checked={ignoreOpen} onChange={setIgnoreOpen}>
                    Ignore open commands
                </Checkbox>
            </div>
            <div className={classNames(layout.horizontalWrap, styles.lockContainer)}>
                {Object.entries(lockStatus).map(([lock, status]) => (
                    <LockItem key={lock} mockController={mockController} lock={lock} status={status}/>
                ))}
            </div>
        </Tool>
    );
}


interface LockItemProps {
    lock: string;
    status: LockStatus;
    mockController: MockController;
}

function LockItem({mockController, lock, status}: LockItemProps) {
    const handleClick = useCallback(() => {
        if (status == LockStatus.Open) {
            mockController.closeLock(lock);
        }
        else {
            mockController.open(lock);
        }
    }, [status, mockController, lock]);
    return (
        <AwesomeBarItem
            className={styles.lockItem}
            onClick={handleClick}
        >
            <div className={styles.lockLabel}>{lock}</div>
            <Badge className={styles[status]}>{status}</Badge>
        </AwesomeBarItem>
    );
}

const styles = stylesheet({
    lockContainer: {
        width: "67vw",
        padding: "1px 0 0 1px",
    },
    lockItem: {
        border: "1px solid #666",
        borderRadius: "1px",
        margin: "-1px 0 0 -1px",
        padding: "0.25em 0.75em",
        background: "#222",
        width: "calc(25% + 1px)",
        cursor: "pointer",
        $nest: {
            "&:hover": {
                filter: "brightness(1.25)"
            }
        }
    },
    lockLabel: {
        flex: 1,
    },
    Open: {
        backgroundColor: '#ffc107'
    },
    Closed: {

    },
    Blocked: {
        color: '#fff',
        backgroundColor: '#e65100'
    },
    Breakin: {
        color: '#fff',
        backgroundColor: '#c62828'
    }
});